import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import core, { App, GQL, View } from '@gin-betting/core';
import { includes } from 'lodash';
import { createSelector, createStructuredSelector } from 'reselect';

import { CompareStatus } from 'app-constants';
import { Modules, useAsyncModule } from 'async-modules';
import { useCompare } from 'hooks';
import ui from 'modules/ui';
import type { State } from 'types';
import { getTitle } from './utils';

const {
    selectors: { selectOddFormat, selectOddsStatisticView },
} = ui;
const {
    entities: {
        selectors: {
            selectOddWithAvailability,
            selectOdd,
            selectStatisticPercentsByMarket,
            selectStatisticPercentsByMatch,
            selectMarket,
        },
    },
    utils: { getIdsFromOddId },
} = core;

type BetslipMdl = Modules['betslip']['default'];

type PropsByBetslip = ReturnType<
    BetslipMdl['selectors']['selectOddButtonView']
>;

interface Input {
    oddId: string;
}

interface Output {
    titleAttr: string;
    title: string;
    value: number;
    status: GQL.OddStatus;
    isActive: boolean;
    isSelected: boolean;
    statistic: number | null;
    compareStatus: CompareStatus;
    handleToggleOdd: VoidFn;
    oddFormat: App.OddFormat;
    betslipOddId: string;
}

export default function useOddButton({ oddId }: Input): Output {
    const dispatch = useDispatch();
    const betslip = useAsyncModule('betslip');

    const selectProps = useMemo(() => makeSelectProps(betslip), [betslip]);
    const { marketId = '' } = getIdsFromOddId(oddId) || {};

    const {
        odd: currOdd,
        market,
        propsByBetslip: {
            betslipBetId,
            isSelected,
            status: betslipStatus,
            isActive,
        },
        statistic,
        oddFormat,
    } = useSelector((s: State) => selectProps(s, { oddId, marketId }));

    const odd = currOdd || ODD_STUB;

    const { value, status, name } = odd;

    const title = getTitle(market, odd);

    const { compareStatus } = useCompare({ value });

    const isEditableBetslip = includes(
        betslip?.constants.BETSLIP_ODD_STATUSES_TO_PLACE_BET,
        betslipStatus
    );

    const recipientBetslipBetId = isEditableBetslip ? betslipBetId : undefined;

    // NOTE: Uncomment to enable multi betslip!
    // const recipientBetslipBetId =
    //     isSingle || isEditableBetslip ? betslipBetId : initialBetslipBetId;

    const handleDeleteBetslipOdd = useCallback(() => {
        if (!isEditableBetslip || !betslip) return;

        dispatch(betslip.actions.deleteBetslipOdd({ oddId, betslipBetId }));
    }, [dispatch, betslip, betslipBetId, isEditableBetslip, oddId]);

    const handleAddBetslipOdd = useCallback(() => {
        if (!recipientBetslipBetId || !betslip) return;

        dispatch(
            betslip.actions.addBetslipOdd({
                oddId,
                betslipBetId: recipientBetslipBetId,
            })
        );
    }, [dispatch, betslip, recipientBetslipBetId, oddId]);

    const handleToggleOdd = useCallback(
        () => (isSelected ? handleDeleteBetslipOdd() : handleAddBetslipOdd()),
        [isSelected, handleAddBetslipOdd, handleDeleteBetslipOdd]
    );

    return {
        titleAttr: name,
        title,
        value,
        status,
        isActive,
        isSelected,
        statistic,
        compareStatus,
        handleToggleOdd,
        oddFormat,
        betslipOddId:
            betslip?.utils.getBetslipOddId({ oddId, betslipBetId }) || '',
    };
}

const makeSelectProps = (betslip: Modules['betslip']['default'] | null) => {
    const selectPropsByBetslip =
        betslip?.selectors.selectOddButtonView ||
        makeSelectPropsByBetslipStub();

    const selectProps = createStructuredSelector({
        propsByBetslip: selectPropsByBetslip,
        odd: selectOdd,
        oddFormat: selectOddFormat,
        statisticView: selectOddsStatisticView,
        market: selectMarket,
    });

    return (state: State, ownProps: { oddId: string; marketId: string }) => {
        const {
            statisticView: { show, statisticView },
            ...props
        } = selectProps(state, ownProps);

        return {
            statistic: show
                ? makeSelectStatistic(statisticView)(state, ownProps)
                : null,
            ...props,
        };
    };
};

const makeSelectStatistic = (statisticView: App.OddStatisticView) =>
    statisticView === App.OddStatisticView.MATCH
        ? selectStatisticPercentsByMatch
        : selectStatisticPercentsByMarket;

const PROPS_BY_BETSLIP_STUB: Omit<PropsByBetslip, 'isActive'> = {
    betType: GQL.BetType.SINGLE,
    betslipBetId: '',
    isSelected: false,
    status: App.BetslipStatus.INITIAL,
};

const ODD_STUB: View.Odd = {
    status: GQL.OddStatus.CANCELLED,
    competitorIds: [],
    id: '',
    isActive: false,
    name: '',
    value: 1.01,
    competitorId: '',
};

const makeSelectPropsByBetslipStub = () => {
    return createSelector(
        selectOddWithAvailability,
        ({ isActive }): PropsByBetslip => ({
            isActive,
            ...PROPS_BY_BETSLIP_STUB,
        })
    );
};
